// KPIs › Meetings (MEETINGS): the team meetings at `/c/{slug}/kpis/meetings`, and one
// meeting at `/c/{slug}/kpis/meetings/{meetingId}` (its tab, `?tab=preread|notes`, in the URL
// beside the focus). Open to whoever may open the scorecard or the chart (`CadenceGate`); the server
// cuts the series to the ones the caller attends or may see.
import { createRoute, useNavigate } from "@tanstack/react-router";
import { CadenceGate } from "../features/cadence/cadence-parts";
import { MeetingScreen, type MeetingTab } from "../features/cadence/meeting-screen";
import { MeetingsScreen } from "../features/cadence/meetings-screen";
import { useCompany } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { kpisRoute } from "./c.$slug.kpis";

export const meetingsRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "meetings",
  validateSearch: (search: Record<string, unknown>) => validateFocusSearch(search),
  component: function Meetings() {
    const { api } = meetingsRoute.useRouteContext();
    return (
      <CadenceGate what="Meetings">
        <MeetingsScreen api={api} company={useCompany()} />
      </CadenceGate>
    );
  },
});

export const meetingRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "meetings/$meetingId",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...(search.tab === "preread" || search.tab === "notes"
      ? { tab: search.tab as MeetingTab }
      : {}),
  }),
  component: function Meeting() {
    const { api } = meetingRoute.useRouteContext();
    const { meetingId } = meetingRoute.useParams();
    const { tab } = meetingRoute.useSearch();
    const navigate = useNavigate();
    return (
      <CadenceGate what="The meeting">
        <MeetingScreen
          key={meetingId}
          api={api}
          company={useCompany()}
          id={meetingId}
          tab={tab ?? "agenda"}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { tab: _drop, ...rest } = prev;
                return next === "agenda" ? rest : { ...rest, tab: next };
              },
              replace: true,
            } as never)
          }
        />
      </CadenceGate>
    );
  },
});
